import React from "react";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  animation: ${fadeIn} 220ms ease-out both;
`;

const HeaderTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
`;

const HeaderSub = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-top: -8px;
`;

const StepsBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StepItem = styled.div`
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 12px;
  align-items: center;
  padding: 10px 8px;
  border-radius: 8px;
  transition: background 120ms ease;
  background: ${p => p.status === 'current' ? '#f9fafb' : 'transparent'};
`;

const StepBadge = styled.div`
  height: 32px;
  width: 32px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 13px;
  font-weight: 700;
  color: ${p => (p.status === 'complete' ? '#fff' : p.status === 'current' ? '#111827' : '#9ca3af')};
  background: ${p => (p.status === 'complete' ? 'linear-gradient(135deg, #2c6bed, #6b5cff)' : p.status === 'current' ? '#fff' : '#f3f4f6')};
  border: 2px solid ${p => (p.status === 'complete' ? 'transparent' : p.status === 'current' ? '#2c6bed' : '#e5e7eb')};
  box-shadow: ${p => (p.status === 'complete' ? '0 2px 8px rgba(44, 107, 237, 0.25)' : 'none')};
`;

const StepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StepTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${p => (p.status === 'complete' || p.status === 'current' ? '#111827' : '#6b7280')};
`;

const StepDesc = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

// Removed inter-step connector lines per design request

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
  border-top: 1px solid #f3f4f6;
  margin-top: 4px;
`;

const Caption = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const PrimaryButton = styled.button`
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid #2c6bed;
  background: linear-gradient(90deg, #2c6bed, #6b5cff);
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(44, 107, 237, 0.2);
  transition: transform 120ms ease, box-shadow 120ms ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(44, 107, 237, 0.3);
  }
  &:active {
    transform: translateY(0);
  }
`;

/**
 * CampaignStepper
 * props:
 * - steps: [{ key, title, description, status: 'complete'|'current'|'upcoming' }]
 * - currentIndex: number
 * - onPrimaryAction: () => void
 * - primaryActionLabel: string
 * - caption?: string
 */
export default function CampaignStepper({ steps = [], currentIndex = 0, onPrimaryAction, primaryActionLabel, caption }) {
  const completedCount = steps.filter(s => s.status === 'complete').length;
  return (
    <Wrap>
      <HeaderTitle>Campaign Progress</HeaderTitle>
      <HeaderSub>{completedCount} of {steps.length} steps completed</HeaderSub>
      <StepsBar>
        {steps.map((s, idx) => (
          <StepItem key={s.key} status={s.status}>
            <StepBadge status={s.status}>
              {s.status === 'complete' ? '✓' : idx + 1}
            </StepBadge>
            <StepText>
              <StepTitle status={s.status}>{s.title}</StepTitle>
              <StepDesc>{s.description}</StepDesc>
            </StepText>
          </StepItem>
        ))}
      </StepsBar>
      <Footer>
        {caption && (
          <Caption>{caption}</Caption>
        )}
        {primaryActionLabel && (
          <PrimaryButton type="button" onClick={onPrimaryAction}>
            {primaryActionLabel}
          </PrimaryButton>
        )}
      </Footer>
    </Wrap>
  );
}


